import { memo, useCallback, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'states/hooks'
import LogoImgLight from 'assets/images/svg/crewworks-slim-light.svg'
import LogoImgDark from 'assets/images/svg/crewworks-slim-dark.svg'
import {
  UserPendingInfo,
  useSignupExistingAccount,
} from './useSignupExistingAccount'
import classNames from 'classnames'
import { CrewTextBoxField } from 'components/forms/crewTextBoxField'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { CrewReCaptcha } from 'components/elements/crewReCaptcha/crewReCaptcha'
import { useTranslation } from '@crew/modules/i18n'
import { useParams } from 'react-router-dom'
import { useCrewNavigate } from 'hooks/useCrewNavigate'
import { useWebsocketForceDisconnect } from '@crew/providers/websocket'
import { useReCaptcha } from 'hooks/useReCaptcha'
import { FormValues } from './useSignupExistingAccount'
import { setLoggedInUser } from 'features/app/states/appSlice'
import { resetReduxState } from 'states/store'
import { CrewCheckBoxField } from 'components/forms/crewCheckBoxField'
import { CrewTermsOfServiceAndPrivacyPolicyLink } from 'components/elements/crewTermsOfServiceAndPrivacyPolicyLink/crewTermsOfServiceAndPrivacyPolicyLink'
import { useGetTenantQuery } from '@crew/apis/tenantSetting/tenantSettingApis'
import {
  CrewAvatar,
  CrewAvatarShapeType,
} from 'components/elements/crewAvatar/crewAvatar'
import { generateImageAvatarUrl } from '@crew/utils/avatar'
import { EntityType } from '@crew/enums/domain'

export type SignupExistingAccountProps = {
  userPending: UserPendingInfo | null
}

export const SignupExistingAccount = memo(
  (props: SignupExistingAccountProps) => {
    const themeMode = useAppSelector((state) => state.app.currentTheme)
    const LogoImg = useMemo(
      () => (themeMode === 'dark' ? LogoImgDark : LogoImgLight),
      [themeMode]
    )

    const {
      control,
      handleSubmit,
      formState,
      validateRules,
      watch,
      joinTenant,
      login,
      isLoadingNewUserJoinTenant,
      isLoadingLogin,
    } = useSignupExistingAccount(props.userPending)

    // Get tenant information
    const { data: getTenant } = useGetTenantQuery()

    // tenant avatar url
    // TODO: Web: generateImageAvatarUrlのリファクタリング
    // https://break-tmc.atlassian.net/browse/CREW-9777
    const tenantAvatarUrl = generateImageAvatarUrl(EntityType.Tenant)

    const errorMessageBaseClassNames = useMemo(() => {
      return 'crew-error-text text-center'
    }, [])

    const { t } = useTranslation()

    const { invitationToken } = useParams()

    const { navigate } = useCrewNavigate()

    const dispatch = useAppDispatch()

    const websocketForceDisconnect = useWebsocketForceDisconnect()

    const [serverErrorMessage, setServerErrorMessage] = useState('')

    const { isReCaptchaVerified } = useReCaptcha()

    // 利用規約とプライバシーポリシーへの同意チェック状態監視
    const agree = watch('agree')

    const canSend = useMemo(
      // formState.isValidはerrorsが空でもfalseになることがあるためerrorsで判定する
      () =>
        formState.isDirty &&
        Object.keys(formState.errors).length === 0 &&
        isReCaptchaVerified &&
        !isLoadingNewUserJoinTenant &&
        !isLoadingLogin,
      // formStateはproxyなのでformState自体をlistenする必要がある
      // https://react-hook-form.com/api/useform/formstate
      [
        formState,
        isReCaptchaVerified,
        isLoadingNewUserJoinTenant,
        isLoadingLogin,
      ]
    )

    // 「ログインして参加する」ボタンクリック
    const handleSubmitButtonClick = useCallback(() => {
      const onSubmit = async (values: FormValues) => {
        try {
          if (!invitationToken) return

          await joinTenant(values, invitationToken)

          //join tenant success, go to new arrival page
          // 念のためログイン前にwebsocketを強制切断しておく
          websocketForceDisconnect()

          // ログイン
          const response = await login(values.loginId, values.password)

          // if 2 step login enable so data user may undefined so need check response have data here
          if (!response.data) {
            setServerErrorMessage(t('message.auth.failedToLogin'))
            return
          }

          // reduxを初期化
          resetReduxState()

          // 秘匿ではないuser情報を保存
          dispatch(setLoggedInUser(response.data))

          // Redirect to new arrival page
          navigate('/newArrival')
        } catch (err) {
          setServerErrorMessage(t('message.auth.failedToLogin'))
        }
      }
      handleSubmit(onSubmit)()
    }, [
      handleSubmit,
      invitationToken,
      joinTenant,
      websocketForceDisconnect,
      login,
      dispatch,
      navigate,
      t,
    ])

    return props.userPending ? (
      <div className="flex flex-row h-[100dvh] pt-4 sm:pt-12 justify-center crew-bg-gray-1">
        <div className="flex flex-col gap-4 sm:gap-6 w-full max-w-sm justify-center">
          <div className="flex flex-col justify-center gap-y-2.5 items-center">
            {/* Logo */}
            <img src={LogoImg} alt="logo" className="mx-auto h-16 w-auto" />
            <p className="text-center">
              {t('label.signupExistingUserMessage')}
            </p>

            <div className="flex items-center gap-x-2">
              {/* Tenant avatar */}
              <CrewAvatar
                displayName={getTenant?.tenant?.name ?? '-'}
                imageURL={tenantAvatarUrl}
                shape={CrewAvatarShapeType.Square}
                cacheValue={
                  getTenant?.tenant // 型制約上nullチェックが必要なためチェックを行うが、基本はnullになることはない
                    ? getTenant.tenant.id + getTenant.tenant.version
                    : ''
                }
              />

              {/* Tenant name */}
              <p className="text-2xl">{getTenant?.tenant?.name}</p>
            </div>
            <p className="text-center">
              {t('label.signupExistingUserMessage2', {
                email: props.userPending.destinationEmail,
              })}
            </p>
          </div>
          <form className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              {/* サーバーエラーメッセージ */}
              <div
                className={classNames(errorMessageBaseClassNames, {
                  'h-4': serverErrorMessage.length > 0,
                })}
                data-testid="login-form-server-error-message"
              >
                {serverErrorMessage}
              </div>
              {/* ログインID */}
              <div>
                <CrewTextBoxField
                  id="loginId"
                  name="loginId"
                  control={control}
                  placeholder={t('label.userIdOrEmailAddress')}
                  labelMode="floating"
                  rules={validateRules.loginId}
                  inputAttr={{
                    'data-testid': 'username',
                  }}
                  showLabel={false}
                />
              </div>

              {/*  氏名 */}
              <div>
                <CrewTextBoxField
                  id="fullName"
                  name="fullName"
                  control={control}
                  placeholder={t('label.displayName')}
                  labelMode="floating"
                  rules={validateRules.fullName}
                  inputAttr={{
                    'data-testid': 'username',
                  }}
                  showLabel={false}
                />
              </div>

              {/* パスワード */}
              <div>
                <CrewTextBoxField
                  id="password"
                  name="password"
                  control={control}
                  placeholder={t('label.password')}
                  labelMode="floating"
                  rules={validateRules.password}
                  mode="password"
                  inputAttr={{
                    'data-testid': 'password',
                  }}
                  showLabel={false}
                />
              </div>

              {/* 「利用規約とプライバシーポリシーに同意します」 */}
              <div className="flex flex-row justify-center gap-3">
                <CrewCheckBoxField
                  id="agree"
                  name="agree"
                  control={control}
                  rules={validateRules.agree}
                />
                <CrewTermsOfServiceAndPrivacyPolicyLink />
              </div>
            </div>

            {/* ログインして参加する */}
            <div className="flex flex-row justify-center">
              <CrewButton
                className="grow"
                type="primary"
                disabled={!canSend || !agree}
                data-testid="loginToJoin"
                text={t('action.loginToJoin')}
                onClick={handleSubmitButtonClick}
              />
            </div>

            {/* Recaptcha */}
            <CrewReCaptcha />
          </form>
        </div>
      </div>
    ) : null
  }
)
